// Chat state machine for the Workspace.
//
// Owns:
//   - `messages`: the conversation thread (server-authoritative, mutated
//     optimistically on send so the user sees their turn instantly)
//   - `sending`:  whether a send is in flight (disables the Send button)
//   - `runStatus`: rich status while a build run is streaming
//     (iteration, latest event kind). null when no run is active.
//   - `error`:    last operation's error (cleared on next successful send)
//
// Send dispatch:
//   - mode = "chat"  → POST /chat (single round-trip; replace messages
//                      with server-authoritative thread on resolve)
//   - mode = "build" → POST /run  → open SSE stream → append assistant
//                      turns as they arrive → close on `run-end` event
//
// On run-end the hook calls `onRunComplete` (if provided) so the
// surrounding component can refetch app data (the server has flipped
// `app.run_succeeded` and the preview iframe must pick it up).
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getThread, getApp, postChat, postRun, postRunPart, postRunAnswer, postRunQueued, closePlan, subscribeRunEvents, abortRun,
  queueMessage, editQueuedMessage, removeQueuedMessage,
  type Message,
} from "../api";
import { autoSendAfter, inboxFromServer, type QueuedMessage } from "../lib/operator_inbox";
import { askViewFromServer, type AskView } from "../lib/ask_view";
import { type RunAttachments } from "../lib/run_body";

// Which agent (or phase) emitted the current action. The chip is
// rendered alongside the label so the operator can see at a glance
// whether backend or frontend is acting — important during the
// parallel-build phase when both agents are working concurrently
// and updates interleave on the single action indicator.
//
// Absent for single-agent runs (the legacy /run flow); set on
// parallel+merge events the new POST /apps/:slug/parallel-merge
// endpoint emits.
export type ActionChip = "backend" | "frontend" | "merge";

// The single current action the LLM is performing right now. Non-null
// only while a build run is streaming AND an action is in flight; null
// otherwise. Each new tool-call / edit event replaces this — we never
// show a list, only the latest live thing.
export type CurrentAction = {
  label: string;     // one-line, e.g. "view src/app.ts", "deploy", "edit api/users.ts"
  // Optional role/phase chip — present on parallel+merge events,
  // absent on single-agent runs.
  chip?: ActionChip;
};

// Backend-only. Frontend agent is server-side refused (see
// tool_dispatcher.ask_allowed) so an operator dialog only ever pauses
// the backend. The composer shows Send (not Abort) while this is
// non-null so the operator can actually reply — without it, `runActive`
// stays true through the whole pipeline and Abort is the only button.
//
// options: 2-4 non-empty strings, order-preserving; the model chose
//          them (see prompts.ts v87 + run_loop.parseAskInput).
// multiSelect: false → single radio-style pick; true → checkboxes.
//
// The UI ALWAYS renders a free-form textarea underneath as an escape
// hatch — operators are never forced into the model's suggestions.
// The question shown to the operator — the server's view of it (lib/ask_view), with its identity
// once the server has queued it.
export type AskPending = AskView;

export type UseChatResult = {
  messages:       Message[];
  loading:        boolean;          // initial thread fetch
  sending:        boolean;          // a send is in flight (button disabled)
  // True while a build run is streaming. The composer disables Send;
  // the Abort button becomes visible. Goes back to false the moment
  // the run-end event fires (or the SSE stream closes).
  runActive:      boolean;
  // Non-null while a `question` SSE event has arrived and hasn't been
  // answered yet. Overrides `runActive` at the composer: when
  // askPending is set, the composer shows Send (with the model's
  // clickable options) so the operator can reply — even though the
  // pipeline is technically still active. Cleared on the next send()
  // start OR when the run terminates (run-end).
  askPending:     AskPending | null;
  error:          string | null;
  // The operator's Chat|Act toggle decides the mode: "chat" = grounded read-only
  // answer, "act" = make the change (server auto-picks build vs edit from phase).
  /** `joints` : les captures déjà réduites et les documents déjà réduits à leur TEXTE
   *  (voir useAttachments). Absent
   *  ou vide ⇒ la requête part exactement comme avant. */
  send:           (prompt: string, mode: "chat" | "act", partId?: string, optionIndex?: number, joints?: RunAttachments) => Promise<void>;
  /** Start ONE named part of the plan — the operator clicked it in the strip. */
  startPart:      (partId: string) => Promise<void>;
  /**
   * Answer a pending question by CLICKING an option. One gesture: this sends.
   *
   * The INDEX travels, not the label. Writing the label into the composer and making the operator
   * press Send threw away the one certain fact — which option was clicked — and left the server
   * reading it back out of a sentence.
   */
  answerOption:   (optionIndex: number, text?: string) => Promise<void>;
  /** The operator is done with this plan: close it, and the strip goes away. */
  closeThePlan:   () => Promise<void>;
  /** What the operator wrote while a run worked, waiting for it to end (lib/operator_inbox). */
  inbox:          QueuedMessage[];
  /** Queue a message while a run works. "no-run": the run had ended — NOTHING was queued, and the
   *  caller keeps the words: an answer may now be owed, and they must not become it. */
  queue:          (text: string) => Promise<"queued" | "no-run">;
  editQueued:     (id: string, text: string) => Promise<void>;
  removeQueued:   (id: string) => Promise<void>;
  /** Send what waits now — for a queue left behind (a reload, a restart) with no run working. */
  sendQueuedNow:  () => Promise<void>;
  // Operator-triggered cancellation. Calls the server's abort endpoint
  // for the current run. The server trips the AbortSignal; the loop
  // exits cleanly; the SSE stream emits a final run-end and closes.
  // No-op if no run is active.
  abort:          () => Promise<void>;
};

// unwrapToInnerRunEvent — normalize an SSE event so downstream
// `e.kind === "X"` checks work uniformly for legacy single-agent
// AND parallel+merge runs.
//
// Parallel+merge runs wrap each per-agent RunEvent in nested
// pipeline shells: `pipeline-event` → `parallel-build-event` →
// `role-event` → underlying RunEvent (the actual assistant-message
// / tool-call / question / etc.). The UI's existing handlers all
// check the OUTER kind, which for parallel runs is always
// "pipeline-event" — so the inner assistant-message never matches
// and the operator never sees the agent's questions or messages.
//
// This helper unwraps when the outer is a pipeline-wrapped role-
// event and returns the inner RunEvent. For pipeline LIFECYCLE
// markers (parallel-build-started, merge-skipped, pipeline-final)
// it returns the event verbatim — those are NOT RunEvents and
// shouldn't be mistaken for one. For legacy single-agent events
// (no pipeline wrapper) it also returns the event verbatim.
//
// Exported for unit-testability + so the chip logic + handler
// logic both consume one canonical unwrap rule.
// readAskPendingFromEvent — if this UNWRAPPED RunEvent is a
// v87 `question`, return its {question, options, multiSelect}. Else
// null. Pure + defensive: an event with a missing/malformed field
// returns null rather than half-populated data — a broken payload
// downstream would render "click one of undefined" and freeze the
// operator. Rejecting here keeps the composer on Abort until a
// valid question arrives (a bug worth flagging via events{}, not
// silently accommodating).
export function readAskPendingFromEvent(e: any): AskPending | null {
  if (!e || e.kind !== "question") return null;
  const text = typeof e.text === "string" ? e.text : null;
  const options = Array.isArray(e.options)
    ? (e.options as unknown[]).filter((o): o is string => typeof o === "string" && o.length > 0)
    : null;
  const multiSelect = typeof e.multiSelect === "boolean" ? e.multiSelect : null;
  if (!text || !options || options.length < 2 || multiSelect === null) return null;
  return { question: text, options, multiSelect };
}

// readReasoningFromEvent — if this UNWRAPPED RunEvent is a `reasoning`
// event, return its {text, tokens}; else null. Pure + defensive, same
// discipline as readAskPendingFromEvent: an empty/whitespace-only thought
// or a missing text field returns null (no empty "Thought" block ever
// renders), and a non-numeric/negative token count normalizes to 0 rather
// than leaking NaN into the label. Exported so the mapping is unit-tested
// independently of the hook wiring.
export function readReasoningFromEvent(e: any): { text: string; tokens: number } | null {
  if (!e || e.kind !== "reasoning") return null;
  const text = typeof e.text === "string" && e.text.trim() ? e.text : null;
  if (!text) return null;
  const tokens = typeof e.tokens === "number" && Number.isFinite(e.tokens) && e.tokens > 0 ? e.tokens : 0;
  return { text, tokens };
}

// Build the client-synthesized "thinking" thread item from a reasoning
// event. Kept here so the reattach + live handlers produce byte-identical
// items (single source of truth — no drift between the two SSE paths).
// Exported so a test can lock the critical `kind: "reasoning"` discriminant
// the renderer branches on (dropping it would silently break rendering).
export function reasoningMessage(r: { text: string; tokens: number }, ts_ms: unknown): Message {
  return {
    role:             "assistant",
    kind:             "reasoning",
    text:             r.text,
    reasoning_tokens: r.tokens,
    mode:             "build",
    ts:               new Date(typeof ts_ms === "number" ? ts_ms : Date.now()).toISOString(),
  };
}

// ── The platform's own verdict, in the operator's words ─────────────────────
//
// A run ends with the agent's summary — free prose the operator reads as fact.
// The platform independently judges whether the app was PROVED to work
// (lib/e2e_attestation.corroborate) and publishes that as `e2e-corroboration`.
// Nothing rendered it.
//
// canva, 2026-09-06: the frontend closed with "No JSX syntax error was present;
// all tags are properly closed". True of the app, false of the record — the same
// agent had fixed that error forty seconds earlier — and the machine's verdict
// for that run was UNSUPPORTED. The operator saw only the prose.
//
// ALWAYS shown, including when it agrees. Silence-means-confirmed is a rule an
// operator has to know to read; a line they see every run is one they cannot
// misread, and it costs one line.
//
// Plain language on purpose: the operator is non-technical, and "UNSUPPORTED" is
// a word for us, not for them.
export function corroborationMessage(e: any, ts_ms: unknown): Message | null {
  const att = e?.attestation ?? {};
  const n = Number(att.total);
  const counted = Number.isFinite(n) && n > 0 ? ` (${n} test${n === 1 ? "" : "s"})` : "";
  const text =
    e?.verdict === "CONFIRMED"
      ? `Checked by the platform: the app's tests ran and passed${counted}.`
    : e?.verdict === "CONTRADICTED"
      ? "The platform could NOT verify this. The build reported success, but its tests did not run and pass — treat the summary above as unproven."
    : e?.verdict === "UNSUPPORTED"
      ? "The platform has nothing to verify: the app's tests never ran. Whatever the summary above says, nothing was proved."
    : null;
  if (text === null) return null;
  return {
    role: "assistant",
    kind: "verification",
    text,
    mode: "build",
    ts:   new Date(typeof ts_ms === "number" ? ts_ms : Date.now()).toISOString(),
  };
}

// readPlanClosedFromEvent / planClosedMessage — the operator put the plan away.
//
// The titles are taken as the server sent them, and an empty list is fine: a plan closed with
// everything built is a plan that finished, and the line says so with nothing after the comma.
export function readPlanClosedFromEvent(e: any): string[] | null {
  if (!e || e.kind !== "plan-closed") return null;
  return Array.isArray(e.remaining)
    ? (e.remaining as unknown[]).filter((t): t is string => typeof t === "string" && t.trim().length > 0).map((t) => t.trim())
    : [];
}

export function planClosedMessage(remaining: string[], ts_ms: unknown): Message {
  return {
    role: "assistant",
    kind: "plan-closed",
    text: remaining.length === 0
      ? "Plan closed — everything in it was built."
      : `Plan closed — ${remaining.length} part${remaining.length === 1 ? "" : "s"} not built.`,
    mode: "build",
    ts:   new Date(typeof ts_ms === "number" ? ts_ms : Date.now()).toISOString(),
    plan_remaining: remaining,
  };
}

// readFailureFromEvent / failureMessage — the build stopped, and why.
//
// The text is taken as the SERVER SENT IT and never rewritten here. That is deliberate: the sentence
// is authored by lib/failure_notice, which is also where the rule lives that it must not name a
// supplier or mention money. A client that composed its own wording would put that rule in two
// places, and the second copy is where it gets forgotten.
//
// Empty text is refused rather than rendered as a blank row — a bar with nothing in it beside a dead
// run is worse than the silence it replaces, because it looks like the platform tried to say
// something and failed.
export function readFailureFromEvent(e: any): string | null {
  if (!e || e.kind !== "error") return null;
  const text = typeof e.message === "string" && e.message.trim() ? e.message.trim() : null;
  return text;
}

export function failureMessage(text: string, ts_ms: unknown): Message {
  return {
    role: "assistant",
    kind: "failure",
    text,
    mode: "build",
    ts:   new Date(typeof ts_ms === "number" ? ts_ms : Date.now()).toISOString(),
  };
}

// readPlanFromEvent / planMessage — the splitting announced a plan.
//
// Same discipline as readAskPendingFromEvent and readReasoningFromEvent, and for the same measured
// reason: a malformed payload must produce NOTHING rather than a half-filled row. A plan card
// rendering "part undefined" is worse than no card, because the operator would read it as the plan.
//
// A plan with no parts is rejected too. It is not a shape to render politely — it means the server
// sent an announcement for a plan that does not exist, and the honest response is to show nothing
// and let the run speak for itself.
export function readPlanFromEvent(e: any): { requirements: string[]; parts: Array<{ id: string; title: string }> } | null {
  if (!e || e.kind !== "plan") return null;
  // A part needs BOTH to be usable: the title to read, the id to start it. One without the other is
  // half a part, so it is dropped rather than rendered as a row nothing can click.
  const parts = Array.isArray(e.parts)
    ? (e.parts as unknown[])
        .map((p: any) => ({
          id: typeof p?.id === "string" ? p.id.trim() : "",
          title: typeof p?.title === "string" ? p.title.trim() : "",
        }))
        .filter((p) => p.id.length > 0 && p.title.length > 0)
    : [];
  if (parts.length === 0) return null;
  const requirements = Array.isArray(e.requirements)
    ? (e.requirements as unknown[]).map((r: any) => (typeof r?.text === "string" ? r.text.trim() : "")).filter((t) => t.length > 0)
    : [];
  return { requirements, parts };
}

export function planMessage(p: { requirements: string[]; parts: Array<{ id: string; title: string }> }, ts_ms: unknown): Message {
  return {
    role: "assistant",
    kind: "plan",
    // The fallback text any renderer can fall back on, and the line a digest would fold.
    text: `Plan: ${p.parts.length} part${p.parts.length === 1 ? "" : "s"}, built one after another.`,
    mode: "build",
    ts:   new Date(typeof ts_ms === "number" ? ts_ms : Date.now()).toISOString(),
    plan_requirements: p.requirements,
    plan_parts: p.parts,
  };
}

// readPartFromEvent / partMessage — a part opened or closed.
//
// `index`/`total` are validated as a PAIR: an index without a total cannot be rendered as "3 / 10",
// and "3 / undefined" is the kind of half-fact this family of readers exists to refuse. An index
// outside its total is refused too — it can only mean the event and the plan disagree, and a row
// claiming "5 / 3" is one nobody can explain.
export type PartMark = { id: string; index: number; total: number; title: string; state: "running" | "sealed" | "failed"; ms: number | null };

export function readPartFromEvent(e: any): PartMark | null {
  if (!e || e.kind !== "part") return null;
  // The id is required, not decorative: the strip matches markers to parts by it, and a marker that
  // cannot be matched would leave a part showing as "to do" while it was being built.
  const id = typeof e.part_id === "string" && e.part_id.trim() ? e.part_id.trim() : null;
  if (!id) return null;
  const index = e.index, total = e.total;
  if (!Number.isInteger(index) || !Number.isInteger(total)) return null;
  if (total < 1 || index < 1 || index > total) return null;
  const title = typeof e.title === "string" && e.title.trim() ? e.title.trim() : null;
  if (!title) return null;
  const state = e.state === "running" || e.state === "sealed" || e.state === "failed" ? e.state : null;
  if (!state) return null;
  // A duration is carried only when it is a real one. Zero and negatives are dropped rather than
  // shown: see Message.part_ms.
  const ms = typeof e.ms === "number" && Number.isFinite(e.ms) && e.ms > 0 ? e.ms : null;
  return { id, index, total, title, state, ms };
}

export function partMessage(m: PartMark, ts_ms: unknown): Message {
  return {
    role: "assistant",
    kind: "part",
    text: `${m.index} / ${m.total} · ${m.title}`,
    mode: "build",
    ts:   new Date(typeof ts_ms === "number" ? ts_ms : Date.now()).toISOString(),
    part_id: m.id,
    part_index: m.index,
    part_total: m.total,
    part_title: m.title,
    part_state: m.state,
    ...(m.ms === null ? {} : { part_ms: m.ms }),
  };
}

// laneOf — WHICH AGENT emitted this event, or null.
//
// The sibling of unwrapToInnerRunEvent, reading the `role` off the SAME wrapper
// that function unwraps. It exists because the lane has to be stamped in TWO
// places (the live stream and the reattach replay) and those two handlers are
// near-identical by construction — anything computed inline in one of them
// eventually exists in only one of them. One exported helper, one test.
//
// null for a single-agent run, a lifecycle marker, or an already-unwrapped
// event. Absent is a real answer ("unknown"), never a default of "backend".
export function laneOf(e: any): "backend" | "frontend" | null {
  if (!e || e.kind !== "pipeline-event") return null;
  const p = e.pipelineEvent;
  if (!p || p.kind !== "parallel-build-event") return null;
  const be = p.event;
  if (!be || be.kind !== "role-event") return null;
  return be.role === "backend" || be.role === "frontend" ? be.role : null;
}

export function unwrapToInnerRunEvent(e: any): any {
  if (!e || e.kind !== "pipeline-event") return e;
  const p = e.pipelineEvent;
  if (!p || typeof p.kind !== "string") return e;
  if (p.kind !== "parallel-build-event") return e;
  const be = p.event;
  if (!be || be.kind !== "role-event") return e;
  return be.event ?? e;
}
// REMOVED 2026-09-23: `eventToActionLabel` and the `currentAction` it fed.
//
// They powered a pinned line above the composer showing the single tool call in flight. It was a
// DUPLICATE of what the feed already shows, and strictly behind it: the assistant turn that carries
// the tool fences (and therefore the chips) is emitted at run_loop:1401, the tool-call event at 2029 —
// so the chip was always on screen first and the pinned line repeated it a moment later.
//
// It earned its place when the feed was a wall of 300 rows and a pinned line was the only legible
// signal. Grouping changed that: the row being written to stays OPEN, so the chips are visible without
// scrolling, with their lane and in order. The space it occupied is now the plan strip.
//
// The labelling logic is in git if a pinned live label is ever wanted again.

// labelLegacyEvent — the single-agent event labels (tool-call, edit).
// Same content the function returned BEFORE phase 6c added the
// pipeline-wrapper support. Returns just a string (no chip; legacy
// runs don't have role tags).
function labelLegacyEvent(e: any): string | null {
  if (e.kind === "tool-call") {
    const name  = String(e.name ?? "");
    const input = (e.input ?? {}) as Record<string, unknown>;
    switch (name) {
      case "view":       return `view ${input.path ?? ""}`.trim();
      case "list_files": return "list files";
      case "create":     return `create ${input.path ?? ""}`.trim();
      case "delete":     return `delete ${input.path ?? ""}`.trim();
      case "deploy_dev": return "deploy";
      case "call_dev":   return `call ${input.method ?? ""} ${input.path ?? ""}`.trim();
      case "call_ui":    return `call ui ${input.route ?? ""}`.trim();
      case "run_test":   return `test ${input.entry ?? ""}`.trim();
      case "events":     return "events";
      default:           return name || null;
    }
  }
  if (e.kind === "edit") {
    return `edit ${e.path ?? ""}`.trim();
  }
  // GROUNDING — the pre-processor that runs BEFORE the build starts.
  //
  // It took 50.2 seconds in canva's run and emitted nothing, so the operator
  // watched a frozen screen at the one moment they are watching hardest. There
  // is no new component here on purpose: ActionIndicator already renders a
  // pulsing dot and a label, so grounding simply becomes an action like any
  // other, in the same place, with the same affordance.
  //
  // `finished` gets its own short label rather than clearing the indicator: the
  // build's first event arrives moments later and replaces it, so a cleared
  // indicator would just flash the same emptiness this fixes.
  if (e.kind === "grounding") {
    if (e.phase === "started") return "grounding the brief";
    if (e.phase === "finished") {
      const secs = Number.isFinite(e.ms) ? ` (${Math.round(Number(e.ms) / 1000)}s)` : "";
      // Fail-open: grounding that timed out returns the raw prompt, and saying
      // "brief grounded" then would be a small lie about what the build is
      // working from.
      return e.changed ? `brief grounded${secs}` : `grounding skipped${secs}`;
    }
    return null;
  }
  // Test progress rollup — shown right after the deploy/verify that moved it,
  // so the operator watches pass/fail/owed evolve in the run view.
  if (e.kind === "verification-progress") {
    const fail = Number(e.failing) > 0 ? ` · ${e.failing}✗` : "";
    return `tests: ${e.passed ?? 0}✓${fail} · ${e.owed ?? 0} owed (of ${e.total ?? 0})`;
  }
  // A frontend lane phase boundary (platform-service lib/frontend_phases):
  // shell → journeys (one agent per journey — it builds its flow and walks it,
  // one shared deploy per round) → finisher. The journeys' end says how each
  // walk came out and how many rounds it took.
  if (e.kind === "phase") {
    if (e.state === "started") {
      const who = Array.isArray(e.journeys) && e.journeys.length > 0
        ? (e.phase === "journeys" ? ` (${e.journeys.length} agents: ${e.journeys.join(", ")})` : ` (room for ${e.journeys.length}: ${e.journeys.join(", ")})`)
        : "";
      return `phase ${e.phase} started${who}`;
    }
    if (e.state === "ended") {
      // "not-needed": the closing proof was clean, so no finishing agent ran
      // (platform-service lib/finish_without_agent).
      if (e.phase === "finisher" && e.status === "not-needed") return "finisher not needed — the app proved itself";
      const outcomes = e.outcomes && typeof e.outcomes === "object"
        ? " · " + Object.entries(e.outcomes as Record<string, string>).map(([j, o]) => `${j}: ${o}`).join(", ")
        : "";
      const rounds = Number.isFinite(e.rounds) ? ` · ${e.rounds} round(s)` : "";
      return `phase ${e.phase} ended: ${e.status ?? "?"}${outcomes}${rounds}`;
    }
    return null;
  }
  // iteration / tool-result / assistant-message / usage / paused /
  // done / run-end / error: NOT shown in the action indicator.
  return null;
}

// labelPipelineEvent — unwraps a ParallelMergePipelineEvent and
// returns the label + chip. The pipeline event tree:
//
//   ParallelMergePipelineEvent
//     ├─ parallel-build-started / -finished / merge-skipped /
//     │  merge-started / merge-finished / pipeline-final   — phase markers
//     ├─ parallel-build-event { event: ParallelBuildEvent }
//     │    ├─ role-event { role, event: RunEvent }          — model action
//     │    ├─ frontend-started                              — phase signal
//     │    └─ joined                                         — phase signal
//     └─ merge-event { event: MergeOrchestratorEvent }
//          └─ cycle-start / probe-batch / heal-started / merged / spiral / etc.
//
// We surface user-visible labels for everything except pure
// background lifecycle markers (joined, pipeline-final, classified,
// cycle-end — those don't need an action-indicator update).
function labelPipelineEvent(p: any): { label: string; chip?: ActionChip } | null {
  if (!p || typeof p.kind !== "string") return null;

  // Phase markers — emitted directly on the pipeline (not wrapped).
  switch (p.kind) {
    case "parallel-build-started":  return { label: "parallel build started" };
    case "parallel-build-finished": return null;   // covered by merge-started or pipeline-final next
    case "merge-skipped":           return { label: `merge skipped: ${p.reason ?? "(unknown)"}` };
    case "merge-started":           return { label: "merge phase started", chip: "merge" };
    case "merge-finished":          return null;   // pipeline-final has the full picture
    case "pipeline-final":          return null;   // status set elsewhere
  }

  // parallel-build-event wrapper — unwrap and chip with role.
  if (p.kind === "parallel-build-event") {
    return labelParallelBuildEvent(p.event);
  }

  // merge-event wrapper — unwrap and chip with "merge".
  if (p.kind === "merge-event") {
    return labelMergeEvent(p.event);
  }

  return null;
}

// labelParallelBuildEvent — handles the events emitted by the
// parallel orchestrator. role-event carries the actual model
// activity (tool calls, edits) tagged with which agent emitted it.
function labelParallelBuildEvent(e: any): { label: string; chip?: ActionChip } | null {
  if (!e || typeof e.kind !== "string") return null;
  if (e.kind === "role-event") {
    const role = e.role === "backend" || e.role === "frontend" ? (e.role as ActionChip) : undefined;
    const legacyLabel = labelLegacyEvent(e.event);
    if (!legacyLabel) return null;
    return { label: legacyLabel, chip: role };
  }
  if (e.kind === "frontend-started") {
    // WHY it started, because the two answers mean different things to an
    // operator reading the run: "backend sealed" is the sequential schedule (the
    // contract is frozen, so the UI is being built against a finished backend),
    // while spec_done / contract-ready mean the backend is still working and the
    // frontend forked a contract that can still move. An unrecognised reason
    // falls back to the bare label rather than printing a raw enum.
    const why = e.reason === "backend-sealed" ? " (backend sealed)"
              : e.reason === "spec_done"      ? " (spec done)"
              : e.reason === "contract-ready" ? " (contract ready)"
              : "";
    return { label: `frontend agent started${why}`, chip: "frontend" };
  }
  // joined — both agents reached done. No user-visible action.
  return null;
}

// labelMergeEvent — handles MergeOrchestratorEvent. The merge phase
// runs probe-cycles; user-visible events are cycle starts, probe
// batches, heal kickoffs, terminal merged/spiral.
function labelMergeEvent(e: any): { label: string; chip?: ActionChip } | null {
  if (!e || typeof e.kind !== "string") return null;
  switch (e.kind) {
    case "cycle-start":
      return { label: `merge cycle ${e.cycle ?? "?"}`, chip: "merge" };
    case "probe-batch": {
      const n = Array.isArray(e.results) ? e.results.length : 0;
      return { label: `probing ${n} ${n === 1 ? "route" : "routes"}`, chip: "merge" };
    }
    case "heal-started": {
      const role = e.role === "backend" || e.role === "frontend" ? e.role : "?";
      return { label: `heal: ${role}`, chip: "merge" };
    }
    case "merged":
      return { label: "merged ✓", chip: "merge" };
    case "spiral":
      return { label: "merge spiral — giving up", chip: "merge" };
    // classified / heal-finished / cycle-end / probe-batch (with
    // empty results): not surfaced — they're either repetitive of
    // an already-shown event OR background lifecycle.
    default:
      return null;
  }
}

export function useChat(
  slug: string,
  opts: {
    onRunComplete?: () => void;
    // If the server told us (via GET /apps/:slug → active_run_id) that a
    // build is already running for this app+operator, we re-attach the
    // SSE stream on mount instead of starting cold. Without this, a
    // page reload during an active build leaves the UI with no handle
    // on the run — the Stop button never appears.
    initialActiveRunId?: string | null;
  } = {},
): UseChatResult {
  const [messages,      setMessages]      = useState<Message[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [sending,       setSending]       = useState(false);
  const [runActive,     setRunActive]     = useState(false);
  const [askPending,    setAskPending]    = useState<AskPending | null>(null);
  const [error,         setError]         = useState<string | null>(null);
  const [inbox,         setInbox]         = useState<QueuedMessage[]>([]);
  // A run just FINISHED (autoSendAfter): send what waits — once `sending` is down. Set inside the
  // send that is still settling, so it cannot send from there: its own guard would drop it.
  const [autoSendDue,   setAutoSendDue]   = useState(false);

  // We keep the AbortController for the in-flight SSE in a ref so a
  // route change / slug change can cancel the stream cleanly.
  const sseAbort = useRef<AbortController | null>(null);
  /** La prose de la REVUE DES PARCOURS arrive juste après son événement typé — ce drapeau fait
   *  le lien, pour qu'on la marque sans chercher ses mots. Une référence et pas un état : elle
   *  est levée et consommée dans le même gestionnaire, aucun rendu n'en dépend. */
  const revueEnAttente = useRef(false);
  // The active build run's id — set when POST /run returns, cleared
  // on run-end. The Abort button uses this to call the server.
  const activeRunId = useRef<string | null>(null);

  // Initial thread load. Re-runs when the slug changes (route switch).
  // If the server reported an active run for this app+operator (passed
  // in via opts.initialActiveRunId from the route's getApp() fetch), we
  // re-attach the SSE stream from seq 0 so the operator regains the
  // Stop button + live action indicator after a page reload.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMessages([]);
    setAskPending(null);
    setInbox([]);
    getThread(slug)
      .then((r) => {
        if (cancelled) return;
        // What still waits, as the server holds it — a reload must not lose sight of it.
        setInbox(inboxFromServer(r.inbox));
        // THE QUESTION, REBUILT ON RELOAD — the same treatment the plan gets below. The server
        // rebuilds it from the persisted review state, so a reload no longer leaves the operator
        // looking at a brief they were asked to confirm with nothing to confirm it with.
        const served = askViewFromServer(r.ask_pending);
        if (served) setAskPending(served);
        // THE PLAN, REBUILT ON RELOAD.
        //
        // Its items reach the live view as stream events, and the thread persists only agent turns —
        // so before this they vanished on refresh, taking the strip the operator steers with. The
        // server now derives them from the plan's own log and sends them beside the messages; here
        // they go through the SAME readers the live stream uses, so a reloaded page cannot show a
        // different plan from the one that was streamed.
        //
        // Appended AFTER the messages rather than interleaved by timestamp: the strip reads the LAST
        // plan and the markers after it, which this order satisfies exactly, and a thread whose
        // machinery is threaded through the conversation is harder to read for no gain.
        const planItems: Message[] = [];
        for (const e of (r.plan_events ?? [])) {
          const planned = readPlanFromEvent(e);
          if (planned) { planItems.push(planMessage(planned, (e as any)?.ts_ms)); continue; }
          const mark = readPartFromEvent(e);
          if (mark) { planItems.push(partMessage(mark, (e as any)?.ts_ms)); continue; }
          const closed = readPlanClosedFromEvent(e);
          if (closed) planItems.push(planClosedMessage(closed, (e as any)?.ts_ms));
        }
        setMessages([...r.messages, ...planItems]);
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(String(e?.message ?? e)); setLoading(false); } });

    const reattachId = opts.initialActiveRunId;
    let reattachEndStatus: string | null = null;
    if (reattachId) {
      activeRunId.current = reattachId;
      setRunActive(true);
      const ctrl = new AbortController();
      sseAbort.current = ctrl;
      // since=0 → server replays the run's full event buffer so the UI
      // catches up before live events start arriving.
      subscribeRunEvents(slug, reattachId, 0, (e) => {
        if (cancelled) return;
        // Unwrap pipeline-event wrappers (parallel+merge runs) so
        // downstream kind-checks match the actual RunEvent (the
        // assistant-message a wrapped role-event carries, etc.).
        // Legacy single-agent events pass through unchanged.
        const inner = unwrapToInnerRunEvent(e);
        // The model's chain-of-thought for the upcoming answer. It arrives
        // immediately BEFORE its assistant-message, so appending it as its
        // own item puts the collapsible "thinking" block right above the
        // answer — no buffering / cross-agent attribution needed.
        const reasoning = readReasoningFromEvent(inner);
        if (reasoning) setMessages((prev) => [...prev, reasoningMessage(reasoning, inner.ts_ms)]);
        // The plan and its part boundaries: the spine the rest of the feed hangs off. Appended on
        // BOTH SSE paths for the same reason the verdict is — an item that existed only on the live
        // stream would vanish on a reload, which is exactly when an operator goes looking for where
        // a plan got to.
        const planned = readPlanFromEvent(inner);
        if (planned) setMessages((prev) => [...prev, planMessage(planned, inner.ts_ms)]);
        const partMark = readPartFromEvent(inner);
        if (partMark) setMessages((prev) => [...prev, partMessage(partMark, inner.ts_ms)]);
        // A run that dies must SAY so. Until 2026-09-22 this event reached the client and was
        // mapped by nothing, so a build stopped and the thread stayed silent.
        const failure = readFailureFromEvent(inner);
        if (failure) setMessages((prev) => [...prev, failureMessage(failure, inner.ts_ms)]);
        // The platform's own verdict, beside the agent's closing words. Here too:
        // a reattach replays the run's whole buffer, and a verdict that appeared
        // only on the live stream would vanish on a page reload — which is
        // exactly when an operator goes looking for it.
        if (inner.kind === "e2e-corroboration") {
          const verdict = corroborationMessage(inner, inner.ts_ms);
          if (verdict) setMessages((prev) => [...prev, verdict]);
        }
        // LA REVUE DES PARCOURS ARRIVE EN TROIS ÉVÉNEMENTS, et le deuxième porte sa prose.
        //
        // Le serveur émet, d'affilée et sans attente entre les trois (run_loop, bloc
        // journeyReview) : `journey-review` (les parcours en données), puis
        // `assistant-message` (le markdown que le fil garde), puis `question` (les réponses
        // que l'UI propose). La prose n'a donc aucun type propre — et le fil la repliait comme
        // du bavardage, alors que le run est à l'arrêt en attendant que l'opérateur la LISE.
        //
        // On marque donc la prose, et on le fait sur l'événement TYPÉ qui la précède, jamais en
        // cherchant des mots dans son texte : c'est la règle de lib/run_feed, et une revue
        // reconnue par ses mots se perdrait le jour où la formulation change.
        //
        // Ce drapeau tient aussi après un rechargement de page : le serveur rejoue tout le
        // tampon d'événements (`since=0`) à travers ce même gestionnaire, dans le même ordre.
        if (inner.kind === "journey-review") revueEnAttente.current = true;
        if (inner.kind === "assistant-message" && typeof inner.text === "string") {
          // Stamp the lane so a live turn is attributed EXACTLY as the reloaded
          // one is (the server persists the same value) — see laneOf.
          const lane = laneOf(e);
          // CONSOMMÉ ICI, une seule fois : la prose suit immédiatement son événement typé, et
          // un drapeau qui resterait levé marquerait le tour suivant comme une revue.
          const estRevue = revueEnAttente.current;
          revueEnAttente.current = false;
          const assistant: Message = {
            role: "assistant",
            text: inner.text,
            mode: "build",
            ts:   new Date(inner.ts_ms ?? Date.now()).toISOString(),
            ...(estRevue ? { kind: "review" as const } : {}),
            ...(lane ? { lane } : {}),
          };
          setMessages((prev) => [...prev, assistant]);
        }
        // v87: the backend fired ask → pause the composer's Abort mode
        // and surface options so the operator can click a reply. Only
        // sets when the payload is well-formed (see readAskPendingFromEvent).
        const ask = readAskPendingFromEvent(inner);
        if (ask) setAskPending(ask);
        if (e.kind === "run-end") {
          reattachEndStatus = (e as { status?: string }).status ?? "";
          setRunActive(false);
          // A run that ended AWAITING A REPLY keeps its question on screen. Clearing it here was
          // right for a run that is over and wrong for one that is waiting on the operator — and
          // a journey review ends EVERY new app's first run, so the two answers were wiped
          // milliseconds after they appeared. What the operator saw was the brief in the feed and
          // a plain text box: consulted, then given nothing to click.
          if ((e as { status?: string }).status !== "awaiting_reply") setAskPending(null);
          activeRunId.current = null;
          opts.onRunComplete?.();
        }
      }, ctrl.signal).catch(() => { /* a lost stream is judged in settleAfterStream, not here */ })
        .finally(() => {
          if (cancelled) return;
          sseAbort.current = null;
          void settleAfterStream(reattachEndStatus, ctrl.signal.aborted);
        });
    }

    return () => {
      cancelled = true;
      // Abort any in-flight SSE on unmount / slug change. The server
      // run keeps running; the next mount can resume via since=.
      sseAbort.current?.abort();
      sseAbort.current = null;
    };
  }, [slug, opts.initialActiveRunId]);

  // `partId` — the operator CLICKED a part of the plan in the strip instead of typing.
  //
  // Threaded through this function rather than given its own copy of it: everything after the start
  // call is identical (the SSE stream, the reattach, the thread persistence, the cleanup), and this is
  // the run path — the least safe place in the client to have two versions of the same 120 lines. The
  // only differences are the two marked below: no typed text to echo, and a different start call.
  // `optionIndex` — the operator CLICKED one of a pending question's options instead of typing.
  //
  // Threaded through here for the same reason `partId` is: everything after the start call is
  // identical, and the run path is the least safe place in the client to keep two versions of the
  // same 120 lines. The index is what travels — the panel used to write the option's LABEL into
  // the composer and make the operator press Send, so the one certain fact was thrown away and
  // the server read it back out of a sentence with a 40-word English vocabulary. Measured
  // 2026-09-23: "oui", "parfait" and "ça me va" were all taken as change requests.
  // `fromInbox` — the run starts on what the operator QUEUED while the last one worked. Threaded through
  // here for the reason `partId` is: the run path is the least safe place to keep two versions of
  // the same 120 lines. The SERVER takes the words from its own queue (POST /run {from_inbox}); `prompt`
  // is then only what the optimistic bubble shows — the queue as the caller just read it.
  const send = useCallback(async (prompt: string, mode: "chat" | "act", partId?: string, optionIndex?: number, joints?: RunAttachments, fromInbox?: boolean) => {
    const text = prompt.trim();
    if ((!text && !partId && optionIndex === undefined) || sending) return;
    setSending(true);
    setError(null);
    // The operator is replying (or starting a new turn); the pending
    // question is answered by the send itself. Clear so the composer
    // reverts to normal streaming state as soon as the new run kicks
    // off. If the run yields another `question`, that event will set
    // askPending again downstream — no race. Kept aside so a send the
    // server REFUSES (a 400 — e.g. the journey review's "change" button
    // with nothing typed) can put the question back: the answer never
    // landed, so the operator is still being asked.
    const askBefore = askPending;
    setAskPending(null);

    // Optimistic user turn so the message appears instantly. `mode` here is a
    // neutral placeholder — user bubbles render identically either way; on a chat
    // reply we replace the whole thread with the server's canonical version, on an
    // act it stays as the turn that kicked off the run.
    // DIFFERENCE 1: a clicked part has no typed words, so there is no user turn to echo. Inventing a
    // bubble that says what the operator "asked" would put words in their mouth — the part header the
    // run publishes is the honest record of what was started.
    // A clicked ANSWER does echo a bubble — the operator did say something, and the label is
    // what they said. Only a clicked PART has no words of its own.
    const optimistic: Message | null = partId ? null : {
      role: "user",
      text: text || (optionIndex !== undefined && askBefore ? askBefore.options[optionIndex] ?? "" : ""),
      mode: "build",
      ts:   new Date().toISOString(),
    };
    if (optimistic) setMessages((prev) => [...prev, optimistic]);

    try {
      // The operator's toggle decides: CHAT = a grounded read-only answer (never
      // touches code); ACT = make the change (the server auto-picks build vs edit
      // from the app's phase). Explicit chat-vs-act means a question can never be
      // mistaken for a change and vice versa.
      if (mode === "chat") {
        // LES PIÈCES JOINTES PARTENT AUSSI EN MODE CHAT. Elles ne partaient pas, et le trombone
        // s affichait quand même : on joignait une photo, on la voyait dans la boîte, on envoyait,
        // et elle n arrivait nulle part. Une perte silencieuse — corrigée le 2026-10-07.
        const r = await postChat(slug, text, joints);
        setMessages(r.thread);   // server-authoritative (answered; nothing built)
        return;
      }

      // act → kick off the run, then stream events.
      let r: Awaited<ReturnType<typeof postRun>>;
      try {
        // DIFFERENCE 2: the part is named, and its text comes from the plan the SERVER holds.
        r = fromInbox ? await postRunQueued(slug)
          : partId ? await postRunPart(slug, partId)
          : optionIndex !== undefined ? await postRunAnswer(slug, optionIndex, text || undefined, askBefore?.id)
          : await postRun(slug, text, joints);
      } catch (e) {
        // Refused before any run existed: the prompt was not accepted, so the
        // optimistic bubble is withdrawn and the pending question restored.
        if (optimistic) setMessages((prev) => prev.filter((m) => m !== optimistic));
        setAskPending(askBefore);
        throw e;
      }
      if (!r.conflict && r.answered) {
        // RECORDED, and another question waits: show it. No run started, so nothing to stream —
        // the operator's answer stays in the thread, and the next question takes the panel.
        setAskPending(r.next);
        return;
      }
      if (r.conflict) {
        // A build is STILL RUNNING. The prompt was NOT accepted, so the
        // optimistic bubble is a lie — withdraw it — and instead of failing,
        // REATTACH to the live run so the operator sees what is actually
        // happening. (Before: the server killed the live run to start this
        // one; ridepool 2026-09-12.) Starting over is Stop, then Run.
        if (optimistic) setMessages((prev) => prev.filter((m) => m !== optimistic));
        setError(`${r.error} — reattached to it.`);
      }
      // The run took the queue: what waited is now this run's request, shown as the bubble above.
      if (fromInbox && !r.conflict) setInbox([]);
      activeRunId.current = r.run_id;
      setRunActive(true);
      let endStatus: string | null = null;

      const ctrl = new AbortController();
      sseAbort.current = ctrl;
      try {
        await subscribeRunEvents(slug, r.run_id, 0, (e) => {
          // Same unwrap as the reattach handler — pipeline-event
          // wrappers (parallel+merge runs) carry the actual
          // RunEvent inside; unwrap so the assistant-message check
          // below sees it. Legacy events pass through unchanged.
          const inner = unwrapToInnerRunEvent(e);
          // Thinking block for the upcoming answer (arrives right before it —
          // see the reattach handler above for why a standalone item is the
          // robust choice over buffer-and-attach).
          const reasoning = readReasoningFromEvent(inner);
          if (reasoning) setMessages((prev) => [...prev, reasoningMessage(reasoning, inner.ts_ms)]);
          // The plan and its part boundaries: the spine the rest of the feed hangs off. Appended on
          // BOTH SSE paths for the same reason the verdict is — an item that existed only on the live
          // stream would vanish on a reload, which is exactly when an operator goes looking for where
          // a plan got to.
          const planned = readPlanFromEvent(inner);
          if (planned) setMessages((prev) => [...prev, planMessage(planned, inner.ts_ms)]);
          const partMark = readPartFromEvent(inner);
          if (partMark) setMessages((prev) => [...prev, partMessage(partMark, inner.ts_ms)]);
          // A run that dies must SAY so. Until 2026-09-22 this event reached the client and was
          // mapped by nothing, so a build stopped and the thread stayed silent.
          const failure = readFailureFromEvent(inner);
          if (failure) setMessages((prev) => [...prev, failureMessage(failure, inner.ts_ms)]);
          // The platform's own verdict, beside the agent's closing words.
          if (inner.kind === "e2e-corroboration") {
            const verdict = corroborationMessage(inner, inner.ts_ms);
            if (verdict) setMessages((prev) => [...prev, verdict]);
          }
          // Append assistant text turns as they arrive — the server
          // persists them too; we mirror so the UI updates live.
          if (inner.kind === "assistant-message" && typeof inner.text === "string") {
            // Same stamp as the live handler above — one helper, so the two
            // paths cannot attribute the same turn differently.
            const lane = laneOf(e);
            const assistant: Message = {
              role: "assistant",
              text: inner.text,
              mode: "build",
              ts:   new Date(inner.ts_ms ?? Date.now()).toISOString(),
              ...(lane ? { lane } : {}),
            };
            setMessages((prev) => [...prev, assistant]);
          }
          // v87: backend fired ask → composer switches from Abort to
          // Send + shows options. See readAskPendingFromEvent for the
          // strict validation (defensive rejection of half-baked
          // payloads).
          const ask = readAskPendingFromEvent(inner);
          if (ask) setAskPending(ask);
          // run-end is the orchestrator's terminal event — clear the
          // action indicator + notify the consumer so it can refetch
          // app data (run_succeeded may have flipped).
          if (e.kind === "run-end") {
            endStatus = (e as { status?: string }).status ?? "";
            setRunActive(false);
            // Same rule as the re-attach path above: a run that ended AWAITING A REPLY keeps its
            // question and its answers on screen. Both paths, or the panel survives a reload and
            // vanishes on the live stream — which is worse than either alone.
            if ((e as { status?: string }).status !== "awaiting_reply") setAskPending(null);
            activeRunId.current = null;
            opts.onRunComplete?.();
          }
        }, ctrl.signal);
      } finally {
        sseAbort.current = null;
        await settleAfterStream(endStatus, ctrl.signal.aborted);
      }
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setSending(false);
    }
  }, [slug, sending, askPending, opts]);

  // settleAfterStream — what to show once a run's stream has ended, for BOTH
  // handlers (the mount reattach and the live send). Three ways a stream ends:
  //   1. run-end arrived        → the run is over. Clear everything.
  //   2. the operator aborted    → they know. Clear everything.
  //   3. neither: the stream was LOST (reconnects exhausted). The run may well
  //      still be alive on the server. This used to be treated as case 1 — the
  //      composer went back to "send", the operator typed "continue", and the
  //      server killed a healthy run to start a new one (ridepool 2026-09-12).
  //      Now: ASK the server. If the run is still active, keep the run id and the
  //      running state (Stop still works) and say so; if not, it really ended.
  // `endStatus` — the terminal status the run-end event carried, or null when none arrived.
  //
  // It has to reach here, and this is the third place that clears a pending question: the two
  // run-end handlers, and this. Fixing only those two left the panel appearing and vanishing a
  // heartbeat later, when the stream closed — which is exactly what the operator saw, on a brand
  // new app, after both of them were "fixed".
  const settleAfterStream = useCallback(async (endStatus: string | null, aborted: boolean) => {
    const sawRunEnd = endStatus !== null;
    const lostId = activeRunId.current;
    if (!sawRunEnd && !aborted && lostId) {
      let stillRunning = false;
      try { stillRunning = (await getApp(slug)).active_run_id === lostId; } catch { /* server unreachable — assume the worst below */ }
      if (stillRunning) {
        setError("Connection to the build was lost, but it is still running. Reload the page to reattach, or press Stop to end it.");
        return;                          // keep runActive + activeRunId
      }
    }
    setRunActive(false);
    // A run that ended AWAITING A REPLY keeps its question: it is over as a RUN and unfinished as
    // a conversation, and the operator is the one holding it up.
    if (endStatus !== "awaiting_reply") setAskPending(null);
    activeRunId.current = null;
    // …and the question shown is the SERVER's, read now that the run is over. The stream only
    // carried the LAST question asked — two lanes can ask in one run, and the server keeps them
    // all, in order, each with the identity a click must send back (lib/ask_view).
    if (endStatus === "awaiting_reply") {
      try { const fresh = askViewFromServer((await getThread(slug)).ask_pending); if (fresh) setAskPending(fresh); }
      catch { /* the streamed question stays on screen; a reload re-reads it */ }
    }
    // A run that FINISHED sends what waits; a pause or a Stop does not (lib/operator_inbox).
    if (autoSendAfter(endStatus)) setAutoSendDue(true);
  }, [slug]);

  // THE QUEUE, SENT once the run is over and nothing is in flight. The SERVER's queue is read first:
  // another tab may have sent it, and a question may be owed (then nothing is sent — the server would
  // refuse too, and the operator sees the question).
  useEffect(() => {
    if (!autoSendDue || sending || runActive) return;
    setAutoSendDue(false);
    if (inbox.length === 0) return;
    let stale = false;
    void (async () => {
      let fresh: Awaited<ReturnType<typeof getThread>>;
      try { fresh = await getThread(slug); } catch { return; }      // the queue stays; "send now" remains
      if (stale) return;
      const waiting = inboxFromServer(fresh.inbox);
      setInbox(waiting);
      if (waiting.length === 0 || fresh.ask_pending) return;
      void send(waiting.map((m) => m.text).join("\n\n"), "act", undefined, undefined, undefined, true);
    })();
    return () => { stale = true; };
  }, [autoSendDue, sending, runActive, inbox.length, slug, send]);

  const abort = useCallback(async () => {
    const id = activeRunId.current;
    if (!id) return;
    try {
      await abortRun(slug, id);
      // The server's run-end event will arrive over SSE and clear
      // runActive. We don't pre-empt that here — the SSE close is the
      // canonical signal.
    } catch (e: any) {
      setError(String(e?.message ?? e));
    }
  }, [slug]);

  const startPart = useCallback((partId: string) => send("", "act", partId), [send]);
  /**
   * The operator clicked an option. One gesture: this SENDS.
   *
   * `text` rides along for the option that means "and here is what I want changed" — the index
   * says which answer, the words are the request. Empty for a plain confirmation.
   */
  const answerOption = useCallback((optionIndex: number, text = "") => send(text, "act", undefined, optionIndex), [send]);
  // Closing a plan is not a run: no build, no stream. The line is appended LOCALLY on success so the
  // strip disappears at once — and it is the same item a reload rebuilds from the log, through the same
  // reader, so the immediate view and the reloaded one cannot disagree.
  const closeThePlan = useCallback(async () => {
    setError(null);
    try {
      const r = await closePlan(slug);
      if (!r.ok) { setError(r.error ?? "the plan could not be closed"); return; }
      const fresh = await getThread(slug);
      const items: Message[] = [];
      for (const e of (fresh.plan_events ?? [])) {
        const closed = readPlanClosedFromEvent(e);
        if (closed) items.push(planClosedMessage(closed, (e as any)?.ts_ms));
      }
      setMessages((prev) => [...prev, ...items]);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    }
  }, [slug]);
  const queue = useCallback(async (text: string): Promise<"queued" | "no-run"> => {
    setError(null);
    try {
      const r = await queueMessage(slug, text.trim());
      if (r.noRun) return "no-run";
      setInbox(inboxFromServer(r.inbox));
      return "queued";
    } catch (e: any) {
      setError(String(e?.message ?? e));
      throw e;                                      // the caller puts the words back in the composer
    }
  }, [slug]);
  const editQueued = useCallback(async (id: string, text: string) => {
    setError(null);
    try { setInbox(inboxFromServer((await editQueuedMessage(slug, id, text)).inbox)); }
    catch (e: any) { setError(String(e?.message ?? e)); }
  }, [slug]);
  const removeQueued = useCallback(async (id: string) => {
    setError(null);
    try { setInbox(inboxFromServer((await removeQueuedMessage(slug, id)).inbox)); }
    catch (e: any) { setError(String(e?.message ?? e)); }
  }, [slug]);
  const sendQueuedNow = useCallback(() => send(inbox.map((m) => m.text).join("\n\n"), "act", undefined, undefined, undefined, true), [send, inbox]);
  return { messages, loading, sending, runActive, askPending, error, send, startPart, answerOption, closeThePlan, abort, inbox, queue, editQueued, removeQueued, sendQueuedNow };
}
